import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faCopy, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import axios from "axios";
import moment from "moment";

import { StatusCode } from "api/protocols";
import {
  GetPricingSheet,
  PostPricingSheet,
  PutPricingSheet,
} from "api/rpc/2024-04/facilityAdmin/teesheet/pricingSheet";
import {
  DeletePricingSheetProduct,
  PostPricingSheetProduct,
} from "api/rpc/2024-04/facilityAdmin/teesheet/pricingSheetProduct";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { GetDepartments } from "api/rpc/2024-04/facilityAdmin/facility/department";
import { showError } from "redux/actions/ui";
import { ICategories, IDepartments, IProduct, IVariant } from "redux/reducers/models/product";
import { TTeesheetPricingSheet } from "redux/reducers/models/teesheet";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH, TABLET_WIDTH } from "helpers/ScreenSizes";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Callout from "components/callout/Callout";
import Input from "components/form/input/Input";
import { Select } from "components/select/index";
import SelectNew from "components/select/SelectNew";
import FormLayout from "components/form/FormLayout";
import TimePick from "components/timePick/TimePick";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import DataTable from "components/dataTable/DataTable";
import Checkbox from "components/form/checkbox/Checkbox";
import MessageDisplay from "components/messageDisplay/MessageDisplay";
import { ButtonNew } from "components/buttonNew";
import AddFolderProductsModal from "components/folders/AddFolderProductsModal";
import { defaultBrowseFilter, IBrowseFilter } from "components/folders/productBrowseFilters";
import {
  appendUniqueProducts,
  buildSelectAllProductBatch,
  fetchFolderProductSearchPage,
  IFolderProductSearchContext,
} from "components/folders/folderProductSearch";

import PricingSheetProductQuickAdd from "./components/PricingSheetProductQuickAdd";
import PricingSheetCopyProductsModal from "./components/PricingSheetCopyProductsModal";
import {
  CHECK_IN_CONFIG_PATH,
  dateFromFormattedString,
  diffVariantIds,
  findConflictingPricingSheets,
  formatPricingSheetDateRange,
  getStoredApplication,
  getStoredDayLabel,
  getVariantProductIds,
  groupVariantsByProduct,
  IPricingSheetFormState,
  IProductPickerState,
  isMultiVariantProduct,
  mergeVariants,
  normalizeLoadedVariants,
  parsePricingSheetApplication,
  persistPricingSheetFilters,
  PRICING_SHEET_APPLICATIONS,
  PRICING_SHEET_LIST_PATH,
  PRICING_SHEET_PRODUCT_TYPES,
  variantsFromSelectedProducts,
} from "./pricingSheetApplications";

import "./teeSheetPricingSheet.scss";

const PRICING_SHEET_SELECT_ALL_LIMIT = 10_000;

const PRICING_SHEET_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
  "Holiday",
] as const;

type PricingSheetDayLabel = (typeof PRICING_SHEET_DAYS)[number];

function isPricingSheetDay(day: string): day is PricingSheetDayLabel {
  return (PRICING_SHEET_DAYS as readonly string[]).includes(day);
}

function sortPricingSheetDays(days: string[]): PricingSheetDayLabel[] {
  return PRICING_SHEET_DAYS.filter(day => days.includes(day));
}

function formatConflictSheetTitles(titles: string[]): string {
  if (titles.length === 0) {
    return "";
  }

  if (titles.length === 1) {
    return titles[0];
  }

  if (titles.length === 2) {
    return `${titles[0]} & ${titles[1]}`;
  }

  return `${titles.slice(0, -1).join(", ")} & ${titles[titles.length - 1]}`;
}

type TBrowseDepartment = IDepartments & { cateogries?: ICategories[] };

type TBrowseFilterState = {
  search: string;
  browseFilter: IBrowseFilter;
};

export default function TeeSheetPricingSheet() {
  const history = useHistory();
  const { pricingSheetId } = useParams<{ pricingSheetId?: string }>();
  const isEditMode = Boolean(pricingSheetId);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const windowSize = useWindowSize();
  const { getCurrencySymbol } = useCurrencyFacility();
  const facilityStore = useAppSelector(store => store.facilityStore);

  const { Option } = Select;
  const today = new Date();
  const isMultiCourse = (facilityStore.facility?.courses?.length ?? 0) > 1;
  const currencySymbol = getCurrencySymbol(facilityStore?.facility?.default_currency_code ?? "CAD");

  const [isSaving, setIsSaving] = useState(false);
  const [isFormReady, setIsFormReady] = useState(!isEditMode);
  const [initialVariantIds, setInitialVariantIds] = useState<number[]>([]);
  const [copyProductsVisibility, setCopyProductsVisibility] = useState(false);
  const [selectedCopyVariantIds, setSelectedCopyVariantIds] = useState<number[]>([]);
  const [existingPricingSheets, setExistingPricingSheets] = useState<TTeesheetPricingSheet[]>([]);
  const [conflictWarningDismissed, setConflictWarningDismissed] = useState(false);

  const storedDay = getStoredDayLabel();
  const [selectedDays, setSelectedDays] = useState<PricingSheetDayLabel[]>(
    isPricingSheetDay(storedDay) ? [storedDay] : ["Monday"],
  );

  const [sheetState, setSheetState] = useState<IPricingSheetFormState>({
    title: "",
    application: getStoredApplication(),
    day_of_week: getStoredDayLabel(),
    start_time: "",
    end_time: "",
    course_id: null,
    startDate: today,
    endDate: today,
    date_range: formatPricingSheetDateRange(today, today),
  });

  const [productState, setProductState] = useState<IProductPickerState>({
    addProductVisibility: false,
    selectedProducts: [],
    addedVariants: [],
    productSearchResults: undefined,
    productSearchTotalCount: undefined,
    productSearchPending: false,
    productSearchLoadingMore: false,
    productSearchSelectAllPending: false,
  });

  const [browseFilterState, setBrowseFilterState] = useState<TBrowseFilterState>({
    search: "",
    browseFilter: defaultBrowseFilter,
  });
  const [browseDepartments, setBrowseDepartments] = useState<TBrowseDepartment[]>([]);
  const [browseMetadataLoading, setBrowseMetadataLoading] = useState(false);
  const [uncategorizedCount, setUncategorizedCount] = useState(0);

  const [expandedProductIds, setExpandedProductIds] = useState<Set<number>>(new Set());
  const browseFilterRef = useRef(browseFilterState.browseFilter);
  const prevAddProductsModalOpen = useRef(false);
  const productSearchCancelTokenRef = useRef<ReturnType<typeof axios.CancelToken.source> | null>(null);

  const productVariantGroups = useMemo(
    () => groupVariantsByProduct(productState.addedVariants),
    [productState.addedVariants],
  );

  const excludedVariantIds = useMemo(
    () => productState.addedVariants.map(variant => variant.id).filter((id): id is number => id != null),
    [productState.addedVariants],
  );

  const excludedVariantIdSet = useMemo(() => new Set(excludedVariantIds), [excludedVariantIds]);
  const selectedCopyVariantIdSet = useMemo(() => new Set(selectedCopyVariantIds), [selectedCopyVariantIds]);

  const resolvedCourseId =
    sheetState.course_id ?? facilityStore.facility?.courses?.[0]?.id ?? null;
  const candidateStartDate = moment(sheetState.startDate).format("MM-DD");
  const candidateEndDate = moment(sheetState.endDate).format("MM-DD");

  const conflictingPricingSheets = useMemo(() => {
    const days = isEditMode
      ? sheetState.day_of_week
        ? [sheetState.day_of_week]
        : []
      : selectedDays;

    return findConflictingPricingSheets(
      existingPricingSheets,
      {
        id: sheetState.id,
        startDate: candidateStartDate,
        endDate: candidateEndDate,
        startTime: sheetState.start_time,
        endTime: sheetState.end_time,
        application: sheetState.application,
        courseId: resolvedCourseId,
        days,
      },
      { compareCourse: isMultiCourse },
    );
  }, [
    existingPricingSheets,
    sheetState.id,
    sheetState.application,
    sheetState.day_of_week,
    sheetState.start_time,
    sheetState.end_time,
    candidateStartDate,
    candidateEndDate,
    resolvedCourseId,
    selectedDays,
    isEditMode,
    isMultiCourse,
  ]);

  const conflictingSheetTitles = useMemo(() => {
    const titles = conflictingPricingSheets
      .map(sheet => sheet.title?.trim())
      .filter((title): title is string => Boolean(title));

    return Array.from(new Set(titles));
  }, [conflictingPricingSheets]);

  useEffect(() => {
    setSelectedCopyVariantIds(prev => {
      const next = prev.filter(id => excludedVariantIdSet.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [excludedVariantIdSet]);

  useEffect(() => {
    setConflictWarningDismissed(false);
  }, [
    candidateStartDate,
    candidateEndDate,
    sheetState.start_time,
    sheetState.end_time,
    sheetState.day_of_week,
    sheetState.application,
    resolvedCourseId,
    selectedDays,
  ]);

  useEffect(() => {
    let mounted = true;

    async function loadExistingPricingSheets() {
      const pricingSheetRes = await GetPricingSheet({}, false);

      if (!mounted) {
        return;
      }

      if (pricingSheetRes.status !== StatusCode.OK) {
        dispatch(showError(t("Error loading pricing sheets")));
        setExistingPricingSheets([]);
        return;
      }

      setExistingPricingSheets(pricingSheetRes.data ?? []);
    }

    void loadExistingPricingSheets();

    return () => {
      mounted = false;
    };
  }, [dispatch, t]);

  function getProductSearchContext(): IFolderProductSearchContext {
    return {
      search: browseFilterState.search,
      browseFilter: browseFilterState.browseFilter,
      types: [...PRICING_SHEET_PRODUCT_TYPES],
      variants: true,
    };
  }

  function withSelectableVariants(product: IProduct): IProduct | null {
    const variants = (product.variants ?? []).filter(variant => !excludedVariantIdSet.has(variant.id));
    if (variants.length === 0) {
      return null;
    }

    return {
      ...product,
      variants,
      variant_count: variants.length,
    };
  }

  useEffect(() => {
    if (sheetState.startDate && sheetState.endDate) {
      const date_range = formatPricingSheetDateRange(sheetState.startDate, sheetState.endDate);
      setSheetState(prevState => ({ ...prevState, date_range }));
    }
  }, [sheetState.startDate, sheetState.endDate]);

  useEffect(() => {
    if (!productState.addProductVisibility) {
      productSearchCancelTokenRef.current?.cancel();
      setProductState(prevState => ({
        ...prevState,
        productSearchPending: false,
        productSearchLoadingMore: false,
      }));
      prevAddProductsModalOpen.current = false;
      return;
    }

    const justOpened = !prevAddProductsModalOpen.current;
    prevAddProductsModalOpen.current = true;

    const browseChanged =
      browseFilterRef.current.type !== browseFilterState.browseFilter.type ||
      browseFilterRef.current.id !== browseFilterState.browseFilter.id;
    browseFilterRef.current = browseFilterState.browseFilter;
    const delay = browseChanged ? 0 : 1000;

    let mounted = true;
    productSearchCancelTokenRef.current?.cancel();
    const cancelSource = axios.CancelToken.source();
    productSearchCancelTokenRef.current = cancelSource;

    setProductState(prevState => {
      const hasLoadedResults = (prevState.productSearchResults?.length ?? 0) > 0;

      return {
        ...prevState,
        productSearchPending: true,
        productSearchLoadingMore: false,
        ...(hasLoadedResults ? {} : { productSearchResults: undefined, productSearchTotalCount: undefined }),
      };
    });

    const runFetch = async () => {
      try {
        const { products, totalCount } = await fetchFolderProductSearchPage(
          GetProduct,
          getProductSearchContext(),
          0,
          true,
          cancelSource.token,
          () => dispatch(showError(t("Failed to get products"))),
        );
        if (mounted) {
          setProductState(prevState => ({
            ...prevState,
            productSearchResults: products,
            productSearchTotalCount: totalCount,
            productSearchPending: false,
            productSearchLoadingMore: false,
          }));
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          return;
        }
        if (mounted) {
          setProductState(prevState => ({
            ...prevState,
            productSearchResults: [],
            productSearchTotalCount: 0,
            productSearchPending: false,
            productSearchLoadingMore: false,
          }));
        }
      }
    };

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (justOpened) {
      void runFetch();
    } else {
      timeoutId = global.setTimeout(() => {
        void runFetch();
      }, delay);
    }

    return () => {
      mounted = false;
      cancelSource.cancel();
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [browseFilterState.search, browseFilterState.browseFilter, productState.addProductVisibility]);

  async function loadBrowseMetadata() {
    const showLoading = browseDepartments.length === 0;

    if (showLoading) {
      setBrowseMetadataLoading(true);
    }

    try {
      const [departmentRes, countRes] = await Promise.all([
        GetDepartments({ extended: true }, false),
        GetProduct(
          {
            get_count: true,
            limit: 1,
            types: [...PRICING_SHEET_PRODUCT_TYPES],
            filters: [{ label: "uncategorized", value: [1] }],
          },
          false,
        ),
      ]);

      if (departmentRes.status === StatusCode.OK) {
        setBrowseDepartments((departmentRes.data ?? []).filter(department => department.type === "department"));
      }

      if (countRes.status === StatusCode.OK && countRes.data && !Array.isArray(countRes.data)) {
        const countData = countRes.data as { count?: number };
        setUncategorizedCount(countData.count ?? 0);
      }
    } finally {
      if (showLoading) {
        setBrowseMetadataLoading(false);
      }
    }
  }

  async function loadMoreProducts() {
    let shouldLoad = false;
    let offset = 0;

    setProductState(prev => {
      if (
        prev.productSearchLoadingMore ||
        prev.productSearchPending ||
        !prev.productSearchResults ||
        prev.productSearchTotalCount == null ||
        prev.productSearchResults.length >= prev.productSearchTotalCount
      ) {
        return prev;
      }

      shouldLoad = true;
      offset = prev.productSearchResults.length;
      return { ...prev, productSearchLoadingMore: true };
    });

    if (!shouldLoad) {
      return;
    }

    try {
      const { products } = await fetchFolderProductSearchPage(
        GetProduct,
        getProductSearchContext(),
        offset,
        false,
        undefined,
        () => dispatch(showError(t("Failed to get products"))),
      );
      setProductState(prev => ({
        ...prev,
        productSearchResults: appendUniqueProducts(prev.productSearchResults ?? [], products),
        productSearchLoadingMore: false,
      }));
    } catch {
      setProductState(prev => ({ ...prev, productSearchLoadingMore: false }));
    }
  }

  async function selectAllMatchingProducts() {
    const loaded = productState.productSearchResults ?? [];
    const totalCount = productState.productSearchTotalCount ?? loaded.length;
    const fullyAddedIds = new Set(
      loaded.filter(product => withSelectableVariants(product) == null).map(product => product.id),
    );
    const fullySelectedIds = new Set(
      productState.selectedProducts
        .filter(selected => {
          const source = loaded.find(product => product.id === selected.id) ?? selected;
          const available = withSelectableVariants(source);
          if (available == null) {
            return true;
          }

          const selectedVariantIds = new Set((selected.variants ?? []).map(variant => variant.id));
          return available.variants.every(variant => selectedVariantIds.has(variant.id));
        })
        .map(product => product.id),
    );
    const excludeIds = new Set<number>([...fullyAddedIds, ...fullySelectedIds]);
    const remaining = Math.max(0, PRICING_SHEET_SELECT_ALL_LIMIT - productState.selectedProducts.length);
    const needsRemoteFetch = remaining > 0 && loaded.length < totalCount;

    if (needsRemoteFetch) {
      setProductState(prev => ({ ...prev, productSearchSelectAllPending: true }));
    }

    try {
      const toAdd = await buildSelectAllProductBatch(
        GetProduct,
        getProductSearchContext(),
        loaded,
        totalCount,
        excludeIds,
        remaining,
        () => dispatch(showError(t("Failed to get products"))),
      );

      const selectable = toAdd
        .map(product => withSelectableVariants(product))
        .filter((product): product is IProduct => product != null);

      setProductState(prev => {
        if (selectable.length === 0) {
          return { ...prev, productSearchSelectAllPending: false };
        }

        return {
          ...prev,
          selectedProducts: mergeSelectedProducts(prev.selectedProducts, selectable),
          productSearchSelectAllPending: false,
        };
      });
    } catch {
      setProductState(prev => ({ ...prev, productSearchSelectAllPending: false }));
    }
  }

  function mergeSelectedProducts(existing: IProduct[], incoming: IProduct[]) {
    const next = [...existing];

    incoming.forEach(product => {
      const index = next.findIndex(selected => selected.id === product.id);
      if (index === -1) {
        next.push(product);
        return;
      }

      const existingVariantIds = new Set((next[index].variants ?? []).map(variant => variant.id));
      const mergedVariants = [...(next[index].variants ?? [])];
      (product.variants ?? []).forEach(variant => {
        if (!existingVariantIds.has(variant.id)) {
          mergedVariants.push(variant);
          existingVariantIds.add(variant.id);
        }
      });
      next[index] = { ...next[index], variants: mergedVariants, variant_count: mergedVariants.length };
    });

    return next;
  }

  useEffect(() => {
    if (!isEditMode || !pricingSheetId) {
      return;
    }

    async function loadPricingSheet() {
      setIsFormReady(false);

      const pricingSheetRes = await GetPricingSheet({ id: pricingSheetId, extended: true }, true);

      if (pricingSheetRes.status !== StatusCode.OK || !pricingSheetRes.data?.[0]) {
        dispatch(showError(t("Error loading pricing sheet")));
        history.push(PRICING_SHEET_LIST_PATH);
        return;
      }

      const pricingSheet = pricingSheetRes.data[0];
      const startDate = pricingSheet.start_date ? dateFromFormattedString(pricingSheet.start_date) : today;
      const endDate = pricingSheet.end_date ? dateFromFormattedString(pricingSheet.end_date) : today;
      const loadedVariants = normalizeLoadedVariants(pricingSheet.variants ?? []);

      setSheetState({
        id: pricingSheet.id,
        title: pricingSheet.title ?? "",
        application: parsePricingSheetApplication(String(pricingSheet.application)),
        day_of_week: pricingSheet.day_of_week ?? "",
        start_time: pricingSheet.start_time ?? "",
        end_time: pricingSheet.end_time ?? "",
        course_id: pricingSheet.course_id ?? null,
        startDate,
        endDate,
        date_range: formatPricingSheetDateRange(startDate, endDate),
      });

      setProductState(prevState => ({
        ...prevState,
        addedVariants: loadedVariants,
      }));

      const loadedVariantIds = loadedVariants.map(variant => variant.id).filter((id): id is number => id != null);

      setInitialVariantIds(loadedVariantIds);
      expandProductGroups(getVariantProductIds(loadedVariants.filter(variant => isMultiVariantProduct(variant))));
      setIsFormReady(true);
    }

    void loadPricingSheet();
  }, [isEditMode, pricingSheetId]);

  function handleCopyProductsVisibility() {
    if (selectedCopyVariantIds.length === 0) {
      dispatch(showError(t("A product must be selected")));
      return;
    }

    setCopyProductsVisibility(true);
  }

  function toggleCopyVariant(variantId: number) {
    setSelectedCopyVariantIds(prev =>
      prev.includes(variantId) ? prev.filter(id => id !== variantId) : [...prev, variantId],
    );
  }

  function toggleCopyVariantGroup(variantIds: number[]) {
    setSelectedCopyVariantIds(prev => {
      const allSelected = variantIds.length > 0 && variantIds.every(id => prev.includes(id));
      if (allSelected) {
        const removeIds = new Set(variantIds);
        return prev.filter(id => !removeIds.has(id));
      }

      const next = new Set(prev);
      variantIds.forEach(id => next.add(id));
      return Array.from(next);
    });
  }

  function toggleSelectAllCopyVariants() {
    setSelectedCopyVariantIds(prev => (prev.length === excludedVariantIds.length ? [] : excludedVariantIds));
  }

  function variantIdsFromVariants(variants: Array<Partial<IVariant>>) {
    return variants.map(variant => variant.id).filter((id): id is number => id != null);
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = event.target;
    setSheetState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleSelectChange(value: string | number, id: string) {
    setSheetState(prevState => ({ ...prevState, [id]: value }));
  }

  function setDateRangeStart(date: React.SetStateAction<Date>) {
    setSheetState(prevState => ({
      ...prevState,
      startDate: typeof date === "function" ? date(prevState.startDate) : date,
    }));
  }

  function setDateRangeEnd(date: React.SetStateAction<Date>) {
    setSheetState(prevState => ({
      ...prevState,
      endDate: typeof date === "function" ? date(prevState.endDate) : date,
    }));
  }

  function expandProductGroups(productIds: number[]) {
    if (productIds.length === 0) {
      return;
    }

    setExpandedProductIds(prev => {
      const nextExpandedProductIds = new Set(prev);
      productIds.forEach(productId => nextExpandedProductIds.add(productId));
      return nextExpandedProductIds;
    });
  }

  function toggleProductGroup(productId: number) {
    setExpandedProductIds(prev => {
      const nextExpandedProductIds = new Set(prev);
      if (nextExpandedProductIds.has(productId)) {
        nextExpandedProductIds.delete(productId);
      } else {
        nextExpandedProductIds.add(productId);
      }
      return nextExpandedProductIds;
    });
  }

  function handleBrowseProductsVisibility() {
    const opening = !productState.addProductVisibility;

    setProductState(prevState => ({
      ...prevState,
      addProductVisibility: opening,
      selectedProducts: [],
      ...(opening
        ? {
            productSearchPending: true,
            productSearchResults: undefined,
            productSearchTotalCount: undefined,
          }
        : {}),
    }));

    if (opening) {
      setBrowseFilterState({ search: "", browseFilter: defaultBrowseFilter });
      void loadBrowseMetadata();
    } else {
      setBrowseFilterState({ search: "", browseFilter: defaultBrowseFilter });
    }
  }

  function handleAddProductsBrowseSearchChange(value: string) {
    setBrowseFilterState(prevState => ({ ...prevState, search: value }));
    setProductState(prev => ({ ...prev, productSearchPending: true }));
  }

  function handleAddProductsBrowseFilterChange(browseFilter: IBrowseFilter) {
    setBrowseFilterState(prevState => ({ ...prevState, browseFilter }));
    setProductState(prev => ({ ...prev, productSearchPending: true }));
  }

  function handleSelectProductToAdd(productOrProducts: IProduct | IProduct[]) {
    const products = Array.isArray(productOrProducts) ? productOrProducts : [productOrProducts];
    const selectable = products
      .map(product => withSelectableVariants(product))
      .filter((product): product is IProduct => product != null);

    if (selectable.length === 0) {
      return;
    }

    setProductState(prev => ({
      ...prev,
      selectedProducts: mergeSelectedProducts(prev.selectedProducts, selectable),
    }));
  }

  function handleUnselectProductToAdd(productOrProducts: IProduct | IProduct[]) {
    const products = Array.isArray(productOrProducts) ? productOrProducts : [productOrProducts];

    setProductState(prev => {
      let next = [...prev.selectedProducts];

      products.forEach(incoming => {
        const index = next.findIndex(product => product.id === incoming.id);
        if (index === -1) {
          return;
        }

        const removeIds = new Set((incoming.variants ?? []).map(variant => variant.id));
        if (removeIds.size === 0) {
          next = next.filter(product => product.id !== incoming.id);
          return;
        }

        const remaining = (next[index].variants ?? []).filter(variant => !removeIds.has(variant.id));
        if (remaining.length === 0) {
          next = next.filter(product => product.id !== incoming.id);
        } else {
          next[index] = { ...next[index], variants: remaining, variant_count: remaining.length };
        }
      });

      return { ...prev, selectedProducts: next };
    });
  }

  function handleAddProducts(products: IProduct[]) {
    const incomingVariants = variantsFromSelectedProducts(products);

    if (incomingVariants.length === 0) {
      dispatch(showError(t("A product must be selected")));
      return;
    }

    setProductState(prevState => ({
      ...prevState,
      addedVariants: mergeVariants(prevState.addedVariants, incomingVariants),
      selectedProducts: [],
      addProductVisibility: false,
    }));
    setBrowseFilterState({ search: "", browseFilter: defaultBrowseFilter });
    expandProductGroups(getVariantProductIds(incomingVariants.filter(variant => isMultiVariantProduct(variant))));
  }

  function handleQuickAddVariant(product: IProduct, variant: IVariant) {
    const incomingVariants = variantsFromSelectedProducts([{ ...product, variants: [variant] }]);

    if (incomingVariants.length === 0) {
      dispatch(showError(t("No variants found for this product")));
      return;
    }

    setProductState(prevState => ({
      ...prevState,
      addedVariants: mergeVariants(prevState.addedVariants, incomingVariants),
    }));
    expandProductGroups(getVariantProductIds(incomingVariants.filter(variant => isMultiVariantProduct(variant))));
  }

  function handleRemoveVariant(variantId: number) {
    setProductState(prevState => ({
      ...prevState,
      addedVariants: prevState.addedVariants.filter(variant => variant.id !== variantId),
    }));
  }

  function formatVariantRate(variant: Partial<IVariant>) {
    if (variant.price == null) {
      return "";
    }

    return `${currencySymbol}${Number(variant.price).toFixed(2)}`;
  }

  function renderProductDeleteCell(variant: Partial<IVariant>) {
    return (
      <td className="pricing-sheets-product-row-delete-cell">
        <ButtonNew
          type="text"
          size="medium"
          onClick={() => {
            if (variant.id != null) {
              handleRemoveVariant(variant.id);
            }
          }}
          aria-label={t("Remove")}
        >
          <FontAwesomeIcon icon={faTrashCan} />
        </ButtonNew>
      </td>
    );
  }

  function renderProductSelectCell(
    checked: boolean,
    onChange: () => void,
    indeterminate = false,
  ) {
    if (!isEditMode) {
      return null;
    }

    return (
      <td className="pricing-sheets-product-select-cell">
        <Checkbox size="medium" checked={checked} indeterminate={indeterminate} onChange={onChange} labelHidden />
      </td>
    );
  }

  function renderSingleProductRow(variant: Partial<IVariant>, productTitle: string) {
    const variantId = variant.id;

    return (
      <tr key={variant.id} className="pricing-sheets-product-row">
        {renderProductSelectCell(variantId != null && selectedCopyVariantIdSet.has(variantId), () => {
          if (variantId != null) {
            toggleCopyVariant(variantId);
          }
        })}
        <td />
        <td>
          <span className="pricing-sheets-product-group-title">{productTitle}</span>
        </td>
        <td>{formatVariantRate(variant)}</td>
        {renderProductDeleteCell(variant)}
      </tr>
    );
  }

  function renderVariantRow(variant: Partial<IVariant>) {
    const variantId = variant.id;

    return (
      <tr key={variant.id} className="pricing-sheets-product-group-child-row">
        {renderProductSelectCell(variantId != null && selectedCopyVariantIdSet.has(variantId), () => {
          if (variantId != null) {
            toggleCopyVariant(variantId);
          }
        })}
        <td />
        <td>{variant.title}</td>
        <td>{formatVariantRate(variant)}</td>
        {renderProductDeleteCell(variant)}
      </tr>
    );
  }

  async function savePricingSheet() {
    if (!sheetState.title.trim()) {
      dispatch(showError(t("Title is required")));
      return;
    }

    const daysToCreate = sortPricingSheetDays(selectedDays);
    if (isEditMode) {
      if (!sheetState.day_of_week) {
        dispatch(showError(t("Day of week is required")));
        return;
      }
    } else if (daysToCreate.length === 0) {
      dispatch(showError(t("Day of week is required")));
      return;
    }

    const courseId = sheetState.course_id ?? facilityStore.facility?.courses?.[0]?.id;
    if (!courseId) {
      dispatch(showError(t("Course is required")));
      return;
    }

    setIsSaving(true);

    const startDateAsString = moment(sheetState.date_range[0]).format("MM-DD");
    const endDateAsString = moment(sheetState.date_range[1]).format("MM-DD");

    if (isEditMode && sheetState.id != null) {
      const editRes = await PutPricingSheet(
        {
          id: sheetState.id,
          title: sheetState.title.trim(),
          day_of_week: sheetState.day_of_week,
          start_time: sheetState.start_time,
          end_time: sheetState.end_time,
          course_id: courseId,
          start_date: startDateAsString,
          end_date: endDateAsString,
        },
        true,
      );

      if (editRes.status !== StatusCode.OK) {
        setIsSaving(false);
        dispatch(showError(typeof editRes.data === "string" ? editRes.data : t("Error updating pricing sheet")));
        return;
      }

      const currentVariantIds = productState.addedVariants
        .map(variant => variant.id)
        .filter((id): id is number => id != null);
      const { addedVariantIds, removedVariantIds } = diffVariantIds(initialVariantIds, currentVariantIds);

      if (removedVariantIds.length > 0) {
        const deleteRes = await DeletePricingSheetProduct(
          {
            pricing_sheet_id: sheetState.id,
            variant_id: removedVariantIds,
          },
          true,
        );

        if (deleteRes.status !== StatusCode.OK) {
          setIsSaving(false);
          dispatch(
            showError(
              typeof deleteRes.data === "string" ? deleteRes.data : t("Error removing products from pricing sheet"),
            ),
          );
          return;
        }
      }

      if (addedVariantIds.length > 0) {
        const productRes = await PostPricingSheetProduct(
          {
            pricing_sheet_id: sheetState.id,
            variant_ids: addedVariantIds,
          },
          true,
        );

        if (productRes.status !== StatusCode.OK) {
          setIsSaving(false);
          dispatch(showError(typeof productRes.data === "string" ? productRes.data : t("Error adding products")));
          return;
        }
      }

      setIsSaving(false);
      history.push(PRICING_SHEET_LIST_PATH, {
        showPricingSheetSavedCallout: true,
        application: sheetState.application,
        dayOfWeek: sheetState.day_of_week,
      });
      return;
    }

    const createResponses = await Promise.all(
      daysToCreate.map(day =>
        PostPricingSheet(
          {
            title: sheetState.title.trim(),
            day_of_week: day,
            start_time: sheetState.start_time,
            end_time: sheetState.end_time,
            course_id: courseId,
            application: sheetState.application,
            start_date: startDateAsString,
            end_date: endDateAsString,
          },
          true,
        ),
      ),
    );

    const failedCreate = createResponses.find(response => response.status !== StatusCode.OK);
    if (failedCreate) {
      setIsSaving(false);
      dispatch(
        showError(
          typeof failedCreate.data === "string" ? failedCreate.data : t("Error creating pricing sheet"),
        ),
      );
      return;
    }

    const createdPricingSheetIds = createResponses
      .map(response => response.data?.id as number)
      .filter((id): id is number => id != null);
    const variantIds = productState.addedVariants.map(variant => variant.id).filter((id): id is number => id != null);

    if (variantIds.length > 0 && createdPricingSheetIds.length > 0) {
      const productResponses = await Promise.all(
        createdPricingSheetIds.map(pricingSheetId =>
          PostPricingSheetProduct(
            {
              pricing_sheet_id: pricingSheetId,
              variant_ids: variantIds,
            },
            true,
          ),
        ),
      );

      if (productResponses.some(response => response.status !== StatusCode.OK)) {
        setIsSaving(false);
        dispatch(showError(t("Error adding products")));
        history.push(PRICING_SHEET_LIST_PATH);
        return;
      }
    }

    setIsSaving(false);
    history.push(PRICING_SHEET_LIST_PATH, {
      showPricingSheetSavedCallout: true,
      application: sheetState.application,
      dayOfWeek: daysToCreate[0],
    });
  }

  function toggleSelectedDay(day: string) {
    if (!isPricingSheetDay(day)) {
      return;
    }

    setSelectedDays(prev => (prev.includes(day) ? prev.filter(selected => selected !== day) : [...prev, day]));
  }

  const allProductsSelected =
    excludedVariantIds.length > 0 && selectedCopyVariantIds.length === excludedVariantIds.length;
  const productTableColumns = [
    ...(isEditMode
      ? [
          {
            label: "",
            width: "48px",
            content:
              excludedVariantIds.length > 0 ? (
                <Checkbox
                  id="selectAllPricingSheetProducts"
                  size="medium"
                  checked={allProductsSelected}
                  indeterminate={selectedCopyVariantIds.length > 0 && !allProductsSelected}
                  onChange={toggleSelectAllCopyVariants}
                  labelHidden
                />
              ) : undefined,
          },
        ]
      : []),
    { label: "", width: "48px" },
    { label: t("Product") },
    { label: t("Rate") },
    { label: "", width: "48px" },
  ];

  return (
    <>
      <Page
        className="pricing-sheets-new-page"
        title={isEditMode ? sheetState.title || t("Pricing sheet") : t("New pricing sheet")}
        subtitle={t("Set when a rate applies and which products it covers.")}
        breadcrumbs={[
          { label: t("Settings"), url: "/admin/settings" },
          { label: t("Pricing sheets"), url: PRICING_SHEET_LIST_PATH },
          {
            label: isEditMode ? sheetState.title || t("Pricing sheet") : t("New pricing sheet"),
            url: isEditMode ? `${PRICING_SHEET_LIST_PATH}/${pricingSheetId}` : `${PRICING_SHEET_LIST_PATH}/new`,
          },
        ]}
        primaryAction={{
          content: t("Save changes"),
          action: () => void savePricingSheet(),
          disabled: isSaving || !isFormReady,
        }}
        secondaryActions={[
          {
            content: t("Cancel"),
            action: () => history.push(PRICING_SHEET_LIST_PATH),
            disabled: isSaving,
          },
        ]}
      >
        {isFormReady ? (
          <>
            <Card key={isEditMode ? `sheet-form-${pricingSheetId}` : "sheet-form-new"}>
              <Card.Section>
                {!conflictWarningDismissed && conflictingSheetTitles.length > 0 && (
                  <div className="pricing-sheets-date-conflict-warning">
                    <Callout
                      type="alerts"
                      title={t("Conflicting pricing sheet dates")}
                      content={
                        <span>
                          {t("These dates overlap with")}{" "}
                          <strong>{formatConflictSheetTitles(conflictingSheetTitles)}</strong>
                          {". "}
                          {t("You can still save, but rates may conflict when both sheets apply at the same time.")}
                        </span>
                      }
                      onClose={() => setConflictWarningDismissed(true)}
                    />
                  </div>
                )}

                <FormLayout>
                  <FormLayout.Group>
                    <Input
                      label={t("Name")}
                      placeholder={t("Enter a name for this pricing sheet")}
                      id="title"
                      value={sheetState.title}
                      onChange={handleInputChange}
                    />
                  </FormLayout.Group>

                  <FormLayout.Group>
                    <Select
                      label={t("Rate type")}
                      defaultValue={sheetState.application}
                      disabled={isEditMode}
                      onChange={(value: string | number) => handleSelectChange(value, "application")}
                    >
                      {PRICING_SHEET_APPLICATIONS.map(app => (
                        <Option key={app.value} value={app.value}>
                          {t(app.label)}
                        </Option>
                      ))}
                    </Select>
                  </FormLayout.Group>
                </FormLayout>
              </Card.Section>

              <Card.Section title={t("When this applies")}>
                <FormLayout>
                  <FormLayout.Group>
                    <DatePickerInput
                      label={t("Date range")}
                      months={windowSize.width > TABLET_WIDTH ? 2 : 1}
                      position="left"
                      showQuickOptions={windowSize.width > MOBILE_WIDTH}
                      startingDate={sheetState.startDate}
                      setStartingDate={setDateRangeStart}
                      endingDate={sheetState.endDate}
                      setEndingDate={setDateRangeEnd}
                      monthOnlyCalendar
                      hideDaysOfWeek
                      hideQuickOptions
                    />

                    {isEditMode ? (
                      <Select
                        label={t("Day of week*")}
                        defaultValue={sheetState.day_of_week}
                        onChange={(value: string | number) => handleSelectChange(value, "day_of_week")}
                      >
                        {PRICING_SHEET_DAYS.map(day => (
                          <Option key={day} value={day}>
                            {t(day)}
                          </Option>
                        ))}
                      </Select>
                    ) : (
                      <SelectNew
                        id="pricing-sheet-days"
                        className={
                          selectedDays.length > 0
                            ? "pricing-sheets-day-multiselect has-selection"
                            : "pricing-sheets-day-multiselect"
                        }
                        label={t("Day of week*")}
                        placeholder={
                          sortPricingSheetDays(selectedDays)
                            .map(day => t(day))
                            .join(", ") || t("Select days")
                        }
                        multiValue={selectedDays}
                        onChange={toggleSelectedDay}
                        options={PRICING_SHEET_DAYS.map(day => ({
                          id: day,
                          label: t(day),
                        }))}
                      />
                    )}
                  </FormLayout.Group>

                  <FormLayout.Group>
                    <TimePick
                      value={sheetState.start_time}
                      onChange={timeString => setSheetState(prev => ({ ...prev, start_time: timeString ?? "" }))}
                      label={t("Start time")}
                      size="large"
                    />
                    <TimePick
                      value={sheetState.end_time}
                      onChange={timeString => setSheetState(prev => ({ ...prev, end_time: timeString ?? "" }))}
                      label={t("End time")}
                      size="large"
                    />
                  </FormLayout.Group>

                  {isMultiCourse && (
                    <FormLayout.Group>
                      <Select
                        label={t("Course")}
                        allowClear
                        defaultValue={sheetState.course_id ?? undefined}
                        onChange={(value: string | number) => handleSelectChange(value, "course_id")}
                      >
                        {facilityStore.facility?.courses?.map((course: Record<string, any>, index: number) => (
                          <Option key={index} value={course.id}>
                            {course.long_name}
                          </Option>
                        ))}
                      </Select>
                    </FormLayout.Group>
                  )}
                </FormLayout>
              </Card.Section>
            </Card>

            <Card
              className="pricing-sheets-assign-products-card"
              title={t("Assign products")}
              subtitle={t("Products added here become available to charge at check-in.")}
              titleActions={
                !isEditMode
                  ? undefined
                  : [
                      {
                        content: (
                          <span className="pricing-sheets-check-in-link-content">
                            <span>{t("Set-up at check-in")}</span>
                            <FontAwesomeIcon icon={faArrowRight} />
                          </span>
                        ),
                        action: () => {
                          persistPricingSheetFilters(sheetState.application, sheetState.day_of_week);
                          history.push(CHECK_IN_CONFIG_PATH);
                        },
                        buttonType: "text",
                      },
                    ]
              }
            >
              <Card.Section>
                <div className="pricing-sheets-assign-products-toolbar">
                  <PricingSheetProductQuickAdd
                    onAddVariant={handleQuickAddVariant}
                    addedVariantIds={productState.addedVariants
                      .map(variant => variant.id)
                      .filter((id): id is number => id != null)}
                  />
                  <div className="pricing-sheets-toolbar-actions">
                    <ButtonNew
                      className="pricing-sheets-browse-button"
                      type="secondary"
                      size="medium"
                      onClick={handleBrowseProductsVisibility}
                    >
                      <img src="/public/images/icons/grid.svg" alt="" aria-hidden="true" className="mr-1" />
                      <span>{t("Browse products")}</span>
                    </ButtonNew>
                    {isEditMode ? (
                      <ButtonNew
                        className="pricing-sheets-browse-button"
                        type="secondary"
                        size="medium"
                        onClick={handleCopyProductsVisibility}
                        disabled={selectedCopyVariantIds.length === 0}
                      >
                        <FontAwesomeIcon icon={faCopy} className="mr-1" />
                        <span>{t("Copy products")}</span>
                      </ButtonNew>
                    ) : null}
                  </div>
                </div>

                <div className="pricing-sheets-assign-products-table-area">
                  <DataTable overflow columns={productTableColumns}>
                    {productState.addedVariants.length === 0 ? (
                      <tr>
                        <td
                          className="pricing-sheets-assign-products-empty-cell"
                          colSpan={productTableColumns.length}
                        >
                          <div className="pricing-sheets-assign-products-empty-state">
                            <MessageDisplay
                              centered
                              icon="table-cells"
                              title={t("No products added yet")}
                              content={t("Search or browse to add products that staff can charge at check-in.")}
                              actions={[
                                {
                                  label: t("Browse products"),
                                  action: handleBrowseProductsVisibility,
                                  type: "primary",
                                },
                              ]}
                            />
                          </div>
                        </td>
                      </tr>
                    ) : (
                      productVariantGroups.map(productGroup => {
                        if (!productGroup.isMultiVariant) {
                          const variant = productGroup.variants[0];
                          if (!variant) {
                            return null;
                          }

                          return renderSingleProductRow(variant, productGroup.productTitle);
                        }

                        const isExpanded = expandedProductIds.has(productGroup.productId);
                        const variantCount = productGroup.variants.length;
                        const variantCountLabel =
                          variantCount === 1 ? t("1 variant") : t("{{count}} variants", { count: variantCount });

                        const groupVariantIds = variantIdsFromVariants(productGroup.variants);
                        const selectedGroupCount = groupVariantIds.filter(id => selectedCopyVariantIdSet.has(id)).length;

                        return (
                          <React.Fragment key={productGroup.productId}>
                            <tr className="pricing-sheets-product-group-parent-row">
                              {renderProductSelectCell(
                                groupVariantIds.length > 0 && selectedGroupCount === groupVariantIds.length,
                                () => toggleCopyVariantGroup(groupVariantIds),
                                selectedGroupCount > 0 && selectedGroupCount < groupVariantIds.length,
                              )}
                              <td
                                className="pricing-sheets-product-group-toggle-cell"
                                onClick={() => toggleProductGroup(productGroup.productId)}
                              >
                                <FontAwesomeIcon icon={isExpanded ? ["far", "chevron-up"] : ["far", "chevron-down"]} />
                              </td>
                              <td>
                                <div className="pricing-sheets-product-group-product-cell">
                                  <span className="pricing-sheets-product-group-title">
                                    {productGroup.productTitle}
                                  </span>
                                  <span className="pricing-sheets-product-group-variant-count">
                                    {variantCountLabel}
                                  </span>
                                </div>
                              </td>
                              <td />
                              <td />
                            </tr>

                            {isExpanded ? productGroup.variants.map(variant => renderVariantRow(variant)) : null}
                          </React.Fragment>
                        );
                      })
                    )}
                  </DataTable>
                </div>
              </Card.Section>
            </Card>
          </>
        ) : null}
      </Page>

      {isEditMode && sheetState.id != null ? (
        <PricingSheetCopyProductsModal
          open={copyProductsVisibility}
          pricingSheetId={sheetState.id}
          variantIds={selectedCopyVariantIds}
          onClose={() => setCopyProductsVisibility(false)}
          onCopied={() => setSelectedCopyVariantIds([])}
        />
      ) : null}

      <AddFolderProductsModal
        open={productState.addProductVisibility}
        onClose={handleBrowseProductsVisibility}
        onAdd={handleAddProducts}
        search={browseFilterState.search}
        onSearchChange={handleAddProductsBrowseSearchChange}
        browseFilter={browseFilterState.browseFilter}
        onBrowseFilterChange={handleAddProductsBrowseFilterChange}
        departments={browseDepartments}
        departmentsLoading={browseMetadataLoading}
        uncategorizedCount={uncategorizedCount}
        productsToAdd={productState.selectedProducts}
        availableSlotCount={PRICING_SHEET_SELECT_ALL_LIMIT}
        totalEmptySlotCount={PRICING_SHEET_SELECT_ALL_LIMIT}
        folderProducts={[]}
        productSearchResults={productState.productSearchResults}
        productSearchTotalCount={productState.productSearchTotalCount}
        productSearchPending={productState.productSearchPending}
        productSearchLoadingMore={productState.productSearchLoadingMore}
        productSearchSelectAllPending={productState.productSearchSelectAllPending}
        onLoadMoreProducts={loadMoreProducts}
        onSelectAllMatchingProducts={selectAllMatchingProducts}
        onSelectProduct={handleSelectProductToAdd}
        onUnselectProduct={handleUnselectProductToAdd}
        hideSlots
        enableVariantSelection
        excludedVariantIds={excludedVariantIds}
        title={t("Browse products")}
        supportText={t("Pick what this sheet can charge. Staff select from these at check-in.")}
        onCreateProduct={() => {
          handleBrowseProductsVisibility();
          history.push("/admin/product/new");
        }}
        onGoToProducts={() => {
          handleBrowseProductsVisibility();
          history.push("/admin/product");
        }}
      />
    </>
  );
}
